import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Nullable } from "@jamesgmarks/utilities";
import { ISubscriptions } from "@llws/lift-entity-interfaces";
import { JsonRenderer } from "src/components/pages/Subscriptions/EditorComponents/JsonRenderer";
import {
  CustomConfigSchema,
  DailyRateSchema,
  TCustomConfiguration,
} from "src/components/pages/Subscriptions/form/CustomConfigEditor/customConfigMeta";

interface IDailyRateConfigurationAccordionProps {
  isEditingSubscriptionConfiguration: boolean;
  setIsEditingSubscriptionConfiguration: React.Dispatch<React.SetStateAction<boolean>>;
  baseSubscriptionConfiguration: Nullable<TCustomConfiguration>;
  subscriptionConfiguration: Nullable<TCustomConfiguration>;
  setCustomConfiguration: (config: TCustomConfiguration) => void;
  storeSubscription: Nullable<ISubscriptions>;
  hasErrors?: boolean;
}

export type TDailyRateType = "daily-flat" | "monthly-active-days-flat";

export const dailyRateTypes: TDailyRateType[] = ["daily-flat", "monthly-active-days-flat"];

interface IDailyRateConfigurationEditorProps {
  config: TCustomConfiguration;
  onCancel: () => void;
  onSave: (updatedConfig: Pick<TCustomConfiguration, "dailyRateType">) => void;
}

export const DailyRateConfigurationEditor: React.FC<IDailyRateConfigurationEditorProps> = ({
  config,
  onCancel,
  onSave,
}) => {
  const [draftDailyConfig, setDraftDailyConfig] = useState<TCustomConfiguration>(
    DailyRateSchema.safeParse(config.dailyRateType).success ? { dailyRateType: config.dailyRateType } : {},
  );

  const configErrors = useMemo(() => {
    const errors: string[] = [];
    if (!draftDailyConfig.dailyRateType || !dailyRateTypes.includes(draftDailyConfig.dailyRateType)) {
      errors.push("Invalid daily rate type selected.");
    }
    return errors;
  }, [draftDailyConfig]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" mt={2}><u>Daily Rate Type</u></Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="Rate Type"
          value={draftDailyConfig.dailyRateType}
          onChange={(e) => {
            const parsed = DailyRateSchema.safeParse(e.target.value);
            if (parsed.success) {
              setDraftDailyConfig({
                dailyRateType: parsed.data,
              });
            }
          }}
        >
          {dailyRateTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type === "daily-flat" ? "Daily Flat Rate" : "Monthly Active Days Flat"}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {
        configErrors.length > 0 && (
          <Grid item xs={12}>
            {configErrors.map((msg) => (
              <Alert key={msg} severity="warning">{msg}</Alert>
            ))}
          </Grid>
        )
      }

      <Grid item container spacing={2} justifyContent="center" mt={2}>
        <Grid item>
          <Button variant="contained" color="error" onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            disabled={configErrors.length > 0}
            onClick={() => {
              onSave(draftDailyConfig);
            }}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Grid >
  );
};

export const DailyRateConfigurationAccordion: React.FC<IDailyRateConfigurationAccordionProps> = ({
  isEditingSubscriptionConfiguration,
  setIsEditingSubscriptionConfiguration,
  baseSubscriptionConfiguration,
  subscriptionConfiguration,
  setCustomConfiguration,
  storeSubscription,
}) => {

  const onCopyBaseSubscriptionConfiguration = () => {
    const baseConfig = CustomConfigSchema.safeParse(baseSubscriptionConfiguration);
    if (baseConfig.success) {
      setCustomConfiguration(baseConfig.data);
    }
  };

  const onCopyOriginalSubscriptionConfiguration = () => {
    const config = CustomConfigSchema.safeParse(storeSubscription?.customConfiguration);
    if (config.success) {
      setCustomConfiguration(config.data);
    }
  };

  return (
    <Grid container spacing={1} textAlign="center">
      <Grid item xs={isEditingSubscriptionConfiguration ? 5 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          Base subscription configuration
        </Typography>

        {baseSubscriptionConfiguration ? (
          < JsonRenderer
            obj={baseSubscriptionConfiguration}
            keyReplacements={{ dailyRateType: "Daily Rate Type" }}
          />
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>Select a base subscription with a custom configuration, above.</i>
          </Alert>
        )}
      </Grid>

      <Grid item xs={isEditingSubscriptionConfiguration ? 7 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          <b>Subscription configuration to save...</b>
        </Typography>

        {isEditingSubscriptionConfiguration && subscriptionConfiguration ? (
          <DailyRateConfigurationEditor
            config={subscriptionConfiguration}
            onSave={(updatedConfig) => {
              const config = {
                ...subscriptionConfiguration,
                dailyRateType: updatedConfig.dailyRateType,
              };

              setCustomConfiguration(config);
              setIsEditingSubscriptionConfiguration(false);
            }}
            onCancel={() => setIsEditingSubscriptionConfiguration(false)}
          />
        ) : subscriptionConfiguration ? (
          <Box>
            <JsonRenderer
              obj={subscriptionConfiguration}
              backgroundColor="#d3f3ff"
              keyReplacements={{ dailyRateType: "Daily Rate Type" }}
            />
            <Grid item xs={12} mt={2}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setIsEditingSubscriptionConfiguration(true)}
              >
                Edit
              </Button>
            </Grid>
          </Box>
        ) : baseSubscriptionConfiguration ? (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No configuration specified for subscription.</i>
          </Alert>
        ) : null}

        <Grid item xs={12} mt={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyOriginalSubscriptionConfiguration}
            disabled={!(storeSubscription?.customConfiguration)}
          >
            Use last-saved subscription configuration
          </Button>
        </Grid>

        <Grid item xs={12} my={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyBaseSubscriptionConfiguration}
            disabled={!(baseSubscriptionConfiguration)}
          >
            Copy base subscription configuration
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};